import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { CheckCircleIcon, GraduationCapIcon, UserIcon, XCircleIcon } from '@phosphor-icons/react';
import type { ValidateMagicLinkResponse } from '@repo/trpc';
import { useState } from 'react';

interface InvitationDialogProps {
  open: boolean;
  invitation: ValidateMagicLinkResponse | null;
  isApplying: boolean;
  error: string | null;
  onAccept: () => void;
  onDecline: () => void;
}

/**
 * Reusable dialog for displaying and accepting magic link invitations
 * Used when students click invitation links while already logged in
 */
export default function InvitationDialog({
  open,
  invitation,
  isApplying,
  error,
  onAccept,
  onDecline,
}: InvitationDialogProps) {
  const [showError, setShowError] = useState(true);

  // Don't render if no invitation data
  if (!invitation || !invitation.isValid) {
    return null;
  }

  const isStudentInvitation = invitation.target === 'student';

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onClose={(_, reason) => {
        // Prevent closing by clicking outside
        if (reason === 'backdropClick') {
          return;
        }
      }}
    >
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          {isStudentInvitation ? (
            <GraduationCapIcon size={32} weight="duotone" />
          ) : (
            <UserIcon size={32} weight="duotone" />
          )}
          <Box>
            <Typography variant="h6">🎉 You&apos;ve Been Invited by {invitation.staff?.name || invitation.organization?.name || 'your school'}!</Typography>
            <Typography variant="body2" color="text.secondary">
              Join {invitation.organization?.name || 'your school'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Stack spacing={3}>
          {/* Error Display */}
          {error && showError && (
            <Alert
              severity="error"
              onClose={() => setShowError(false)}
              icon={<XCircleIcon size={20} />}
            >
              {error}
            </Alert>
          )}

          {/* Invitation Details */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Organization
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {invitation.organization?.name || 'Unknown Organization'}
            </Typography>
          </Box>

          {invitation.staff?.name && <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Invited by
            </Typography>
            <Typography variant="body1">
              {invitation.staff.name}
            </Typography>
          </Box>}


          {/* Custom Message */}
          {invitation.invitation?.customMessage && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'primary.50',
                border: '1px solid',
                borderColor: 'primary.200',
              }}
            >
              <Typography variant="subtitle2" color="primary.main" gutterBottom>
                💬 Message from {invitation.staff?.name || 'your school'}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {invitation.invitation.customMessage}
              </Typography>
            </Box>
          )}

          {/* Expiry Info */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'warning.50',
              border: '1px solid',
              borderColor: 'warning.200',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              ⏰ This invitation expires on{' '}
              {new Intl.DateTimeFormat('en-US', {
                dateStyle: 'medium',
                timeStyle: 'short',
              }).format(new Date(invitation.invitation?.expiresAt || Date.now()))}
            </Typography>
          </Box>

          {/* What happens next */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'success.50',
              border: '1px solid',
              borderColor: 'success.200',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <CheckCircleIcon size={20} color="#2e7d32" />
              <Box>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  What happens when you accept?
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20, color: 'rgba(0,0,0,0.6)' }}>
                  <li>
                    <Typography variant="body2" color="text.secondary" component="span">
                      Your account will be connected to {invitation.organization?.name}
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary" component="span">
                      Your teachers will be able to see your progress, and add transcripts, reference letters and other documents to your profile
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary" component="span">
                      You&apos;ll get access to school-specific resources such as scholarships and special events
                    </Typography>
                  </li>
                </ul>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onDecline}
          variant="outlined"
          disabled={isApplying}
          fullWidth
        >
          Not Now
        </Button>
        <Button
          onClick={onAccept}
          variant="contained"
          disabled={isApplying}
          startIcon={isApplying ? <CircularProgress size={20} /> : <CheckCircleIcon size={20} />}
          fullWidth
        >
          {isApplying ? 'Joining...' : 'Accept Invitation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

